import type { CarrierPreviewItem } from '@/types';
import type { OrderRoutingDecision } from './decideOrderRouting';
import type { RuleDispatchContext } from '@/stores/useNewOrderStore';

/**
 * Item 4 — shared rule-decision -> carrier-list reducer.
 *
 * Single source of truth for how an evaluate-shipping-rules decision
 * narrows the available carriers and produces the `ruleDispatch` context
 * surfaced to the SelectCarrier / OrderSummary steps. Used by BOTH the
 * create flow (add-new-order) and the edit flow (edit-order) so the
 * restricted-list / auto-dispatch / auto-cheapest behaviour is identical.
 *
 * Returns:
 *   - carrier` filtered/sorted carrier list (post rule-filter)
 *   - ruleDispatch  context for the transparency banner + override modal,
 *                   or null for the manual / no-match case
 *   - autoSelectIndex  index of the carrier to pre-select (and skip Step 2),
 *                      or null when the merchant must pick manually
 */
export interface RuleDecisionResult {
  carriers: CarrierPreviewItem[];
  ruleDispatch: RuleDispatchContext | null;
  autoSelectIndex: number | null;
}

export function applyRuleDecisionToCarriers(
  available: CarrierPreviewItem[],
  decision: OrderRoutingDecision,
): RuleDecisionResult {
  // Manual / no-match — full list, no banner, no auto-select.
  if (decision.action === 'manual' || !decision.winningRuleUid) {
    return { carriers: available, ruleDispatch: null, autoSelectIndex: null };
  }

  // NOTE (SRE-3): the create-order flow is intentionally unaffected by a rule's
  // is_overridable flag — it behaves the same for overridable and strict rules
  // (auto-dispatch skips Step 2, restricted shows the rule's carriers). The
  // overridable flag only changes the BULK IMPORT carrier step (dropdown vs
  // read-only text). Override is never exercised in single-order create.

  // Restrict the carrier set down to the rule's allowlist (by uid).
  const restrictUids =
    decision.action === 'restricted' || decision.action === 'auto-cheapest'
      ? decision.restrictedCarrierUids
      : decision.action === 'auto-dispatched'
        ? [decision.assignedCarrierUid]
        : null;

  let carriers = available;
  if (restrictUids && restrictUids.length > 0) {
    carriers = available.filter((c) => restrictUids.includes(c.carrier.uid));
  }

  // auto-cheapest — sort the surviving carriers by shipping_fee ascending
  // so index 0 is the cheapest and can be auto-picked.
  if (decision.action === 'auto-cheapest') {
    carriers = [...carriers].sort(
      (a, b) => (parseFloat(a.shipping_fee) || 0) - (parseFloat(b.shipping_fee) || 0),
    );
  }

  const ruleDispatch: RuleDispatchContext = {
    action: decision.action,
    winningRuleUid: decision.winningRuleUid,
    assignedCarrierUid: decision.action === 'auto-dispatched' ? decision.assignedCarrierUid : null,
    assignedCarrierSlug:
      decision.action === 'auto-dispatched' && carriers.length > 0
        ? carriers[0].carrier.slug || null
        : null,
  };

  // auto-dispatched / auto-cheapest skip Step 2 — but only when the
  // mandated carrier actually survived the availability filter.
  const autoSelectIndex =
    (decision.action === 'auto-dispatched' || decision.action === 'auto-cheapest') &&
    carriers.length > 0
      ? 0
      : null;

  return { carriers, ruleDispatch, autoSelectIndex };
}
